/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useSearchParams } from 'react-router';
import { STORE_AVAILABILITY_REFINEMENT } from '@/extensions/bopis/constants';

/**
 * True when the grid is already filtered to a single store's assortment, so every
 * tile in the result set is one that store carries and a pickup indicator on the
 * tile is truthful without any extra availability call.
 *
 * This reads the `c_availableStores` refine, which is a real SCAPI refinement backed
 * by the live per-store inventory lists. It deliberately does NOT read `ilids`: that
 * parameter is not in the SCAPI contract (400 as a query param, silently ignored in
 * `refine=`), so an `ilids`-driven indicator asserted pickup availability that had
 * never been checked against anything. See `STORE_AVAILABILITY_REFINEMENT`.
 */
export function useShowPickupAvailable(): boolean {
    const [searchParams] = useSearchParams();
    return searchParams.getAll('refine').some((value) => value.startsWith(`${STORE_AVAILABILITY_REFINEMENT}=`));
}
